import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";

const sizes = {
  xs: {
    trackWidth: "24px",
    trackHeight: "14px",
    thumbSize: "10px",
    gap: "2px",
    fontSize: tokens.typography.fontSize["md-12px"],
    lineHeight: "16px",
  },
  sm: {
    trackWidth: "28px",
    trackHeight: "16px",
    thumbSize: "12px",
    gap: "2px",
    fontSize: tokens.typography.fontSize["md-12px"],
    lineHeight: "16px",
  },
  md: {
    trackWidth: "34px",
    trackHeight: "20px",
    thumbSize: "16px",
    gap: "2px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
  },
  lg: {
    trackWidth: "40px",
    trackHeight: "24px",
    thumbSize: "20px",
    gap: "4px",
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: "20px",
  },
};

const defaultFocusVisibleState = {
  outline: `1px solid ${tokens.colors.brand.blue["500"]}`,
  boxShadow: "0px 0px 0px 3px rgba(163, 163, 163, 0.50)",
};

const errorState = {
  outline: `1px solid ${tokens.colors.ui.destructive}`,
  boxShadow: "0px 0px 1px 1px rgba(196, 61, 81, 0.20)",
};

const variantConfig = {
  primary: {
    background: tokens.colors.ui.primary,
    color: tokens.colors.ui["primary-foreground"],
    hover: { background: tokens.colors.ui.primary },
    active: { background: tokens.colors.ui.primary },
  },
  secondary: {
    background: tokens.colors.brand.blue["100"],
    color: tokens.colors.brand["aie-blue"]["500"],
    hover: { opacity: 0.80 },
    active: { opacity: 0.80 },
  },
  destructive: {
    background: tokens.colors.states.alert["50"],
    color: tokens.colors.ui.destructive,
    hover: { background: tokens.colors.states.alert["100"] },
    active: { background: tokens.colors.states.alert["50"] },
  },
};

const getVariantStyles = (variant) => {
  const config = variantConfig[variant] || variantConfig.primary;
  const { background, color, hover, active } = config;
  return {
    background,
    color,
    hover,
    active,
  };
};

const StyledHiddenCheckbox = styled.input.attrs({ type: "checkbox" })(() => ({
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
}));

const StyledTrack = styled.span(({ size = "lg", variant = "primary", checked = false }) => {
  const { trackWidth, trackHeight, gap, thumbSize } = sizes[size];
  const variantStyles = getVariantStyles(variant);

  return {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    width: trackWidth,
    height: trackHeight,
    borderRadius: "9999px",
    transition: "all 0.2s ease",
    border: `1px solid ${tokens.colors.ui.border}`,
    background: tokens.colors.ui.background,
    cursor: "pointer",

    ...(checked && {
      background: variantStyles.background,
      borderColor: variantStyles.background,
    }),

    ...(!checked && {
      background: tokens.colors.ui.background,
      borderColor: tokens.colors.ui.border,
    }),

    "&:hover": variantStyles.hover,
    "&:active": variantStyles.active,

    "& .thumb": {
      position: "absolute",
      top: gap,
      left: checked ? `calc(100% - ${thumbSize} - ${gap})` : gap,
      width: thumbSize,
      height: thumbSize,
      borderRadius: "50%",
      background: checked ? variantStyles.color : tokens.colors.neutral.white,
      transition: "all 0.2s ease",
      boxShadow: "0px 0px 0px 1px rgba(163, 163, 163, 0.50)",
    },
  };
});

const StyledWrapper = styled.label(({ size = "lg" }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontSize: sizes[size].fontSize,
  fontWeight: tokens.typography.fontWeights.medium,
  lineHeight: sizes[size].lineHeight,
  color: tokens.colors.ui.foreground,
  cursor: "pointer",

  "&:has(input:disabled)": {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  "input:focus-visible + span": defaultFocusVisibleState,

  'input[data-error="true"] + span': errorState,
}));

const StyledLabelContainer = styled.span(() => ({
  display: "flex",
  flexDirection: "column",
}));

const StyledLabelText = styled.span(({ error = false }) => ({
  color: error ? tokens.colors.ui.destructive : tokens.colors.ui.foreground,
}));

const StyledSubtext = styled.span(() => ({
  fontSize: tokens.typography.fontSize["md-12px"],
  fontWeight: tokens.typography.fontWeights.regular,
  lineHeight: "16px",
  color: tokens.colors.ui["muted-foreground"],
}));

const StyledFieldWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  width: "100%",
}));

const StyledLabel = styled.label(() => ({
  fontSize: tokens.typography.fontSize["lg-14px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.medium,
  lineHeight: "20px",
  color: tokens.colors.ui.foreground,
  display: "flex",
  alignItems: "center",
  gap: "4px",

  '&[data-required="true"]::after': {
    content: '"*"',
    color: tokens.colors.ui.destructive,
    marginLeft: "2px",
  },
}));

const StyledErrorText = styled.span(() => ({
  fontSize: tokens.typography.fontSize["md-12px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.medium,
  lineHeight: "16px",
  color: tokens.colors.ui.destructive,
  minHeight: "1.4em",
}));

const StyledHelperText = styled.span(() => ({
  fontSize: tokens.typography.fontSize["md-12px"],
  fontFamily: tokens.typography.fontFamilies["albert-sans"],
  fontWeight: tokens.typography.fontWeights.regular,
  lineHeight: "16px",
  color: tokens.colors.ui["muted-foreground"],
}));

/**
 * A toggle button (switch) component with support for multiple variants, sizes, and states.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.size="lg"] - Size variant: "xs", "sm", "md", or "lg"
 * @param {string} [props.variant="primary"] - Visual variant: "primary", "secondary", or "destructive"
 * @param {boolean} [props.checked=false] - Whether the toggle is on
 * @param {boolean} [props.disabled=false] - If true, the toggle is disabled
 * @param {boolean} [props.error=false] - If true, applies error styling
 * @param {string} [props.label] - Label text to display next to the toggle
 * @param {string} [props.subtext] - Subtext to display below the label
 * @param {function} [props.onChange] - Callback fired when the toggle state changes
 * @param {React.Ref} ref - Forwarded ref to the underlying input element
 * @returns {JSX.Element} The rendered toggle component
 */
const ToggleButton = forwardRef(
  (
    {
      checked = false,
      onChange,
      size = "lg",
      variant = "primary",
      disabled = false,
      error = false,
      label,
      subtext,
      ...props
    },
    ref
  ) => {
    return (
      <StyledWrapper size={size}>
        <StyledHiddenCheckbox
          ref={ref}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          data-error={error ? "true" : undefined}
          {...props}
        />
        <StyledTrack size={size} variant={variant} checked={checked}>
          <span className="thumb" />
        </StyledTrack>
        {(label || subtext) && (
          <StyledLabelContainer>
            {label && <StyledLabelText error={error}>{label}</StyledLabelText>}
            {subtext && <StyledSubtext>{subtext}</StyledSubtext>}
          </StyledLabelContainer>
        )}
      </StyledWrapper>
    );
  }
);

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;

export {
  StyledFieldWrapper as FieldWrapper,
  StyledLabel as Label,
  StyledErrorText as ErrorText,
  StyledHelperText as HelperText,
};